import { useState, useEffect, useMemo, useRef } from 'react';
import { apiFetch } from '../api';
import { CategoryOptions, getCategoryLabel } from '../categories';

function TagsInput({ tags, allTags, onChange }) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  const suggestions = input.trim()
    ? allTags.filter((t) => t.toLowerCase().includes(input.toLowerCase()) && !tags.includes(t))
    : allTags.filter((t) => !tags.includes(t));

  function addTag(tag) {
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  }

  function removeTag(tag) {
    onChange(tags.filter((t) => t !== tag));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }

  return (
    <div className="stx-tags-input" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {tags.map((tag) => (
          <span key={tag} className="stx-tag-badge">
            {tag}
            <button onClick={() => removeTag(tag)} className="stx-tag-remove">&times;</button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? 'Add tags...' : ''}
          style={{ flex: 1, minWidth: 80, background: 'transparent', border: 'none', color: '#e4e4e7', outline: 'none', fontSize: 13, padding: '2px 4px' }}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="stx-tags-dropdown">
          {suggestions.slice(0, 10).map((tag) => (
            <div key={tag} className="stx-tags-option" onMouseDown={() => addTag(tag)}>{tag}</div>
          ))}
        </div>
      )}
    </div>
  );
}

const SORT_OPTIONS = [
  { key: 'date_desc', label: 'Newest First' },
  { key: 'date_asc', label: 'Oldest First' },
  { key: 'amount_desc', label: 'Amount (High)' },
  { key: 'amount_asc', label: 'Amount (Low)' },
  { key: 'description', label: 'Description' },
];

const PAGE_SIZE = 50;

const SPENDING_TYPES = [
  { value: 'bills', label: 'Normal Bills' },
  { value: 'controllable', label: 'Controllable' },
  { value: 'unplanned', label: 'Unplanned' },
];

const DATE_RANGES = [
  { key: 'this-month', label: 'This Month' },
  { key: 'last-month', label: 'Last Month' },
  { key: '30', label: 'Last 30 Days' },
  { key: '90', label: 'Last 90 Days' },
  { key: 'all', label: 'All' },
];

function TransactionsPanel({ refreshKey, extraRefreshKey }) {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [accountFilter, setAccountFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('unreviewed');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [spendingTypeFilter, setSpendingTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('this-month');
  const [page, setPage] = useState(0);
  const [selectedTx, setSelectedTx] = useState(null);
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoText, setMemoText] = useState('');
  const [savingMemo, setSavingMemo] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Inline editing
  const [inlineCatTx, setInlineCatTx] = useState(null);
  const [inlineStTx, setInlineStTx] = useState(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkSpendingType, setBulkSpendingType] = useState('');

  // Apply rules scope
  const [applyScope, setApplyScope] = useState('uncategorized');
  const [applyingRules, setApplyingRules] = useState(false);
  const [applyRulesResult, setApplyRulesResult] = useState(null);

  // Category configs
  const [categoryConfigs, setCategoryConfigs] = useState([]);

  // Create rule modal
  const [ruleModal, setRuleModal] = useState(null); // { pattern, category, spendingType, applyToAll }
  const [savingRule, setSavingRule] = useState(false);

  useEffect(() => {
    fetchData();
  }, [refreshKey, extraRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    try {
      const [txRes, acctRes, configRes] = await Promise.all([
        apiFetch('/api/simplefin/transactions'),
        apiFetch('/api/simplefin/accounts'),
        apiFetch('/api/category-configs'),
      ]);
      if (txRes.ok) setTransactions(await txRes.json());
      if (acctRes.ok) setAccounts(await acctRes.json());
      if (configRes.ok) setCategoryConfigs(await configRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // Build lookup sets from category configs
  const { hiddenTypeCats, notReportableCats } = useMemo(() => {
    const hidden = new Set();
    const notReportable = new Set();
    for (const cfg of categoryConfigs) {
      if (cfg.hideSpendingType) hidden.add(cfg.category);
      if (cfg.autoReportable === false) notReportable.add(cfg.category);
    }
    return { hiddenTypeCats: hidden, notReportableCats: notReportable };
  }, [categoryConfigs]);

  function shouldHideSpendingType(cat) {
    if (!cat) return false;
    return hiddenTypeCats.has(cat) || [...hiddenTypeCats].some((c) => cat.startsWith(c + ':'));
  }

  useEffect(() => { setPage(0); }, [search, sortBy, accountFilter, reviewFilter, categoryFilter, spendingTypeFilter, dateRange, applyScope]);

  const filtered = useMemo(() => {
    let list = [...transactions];

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate;
      if (dateRange === 'this-month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (dateRange === 'last-month') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        list = list.filter((t) => {
          const d = new Date(t.posted || t.transactedAt || 0);
          return d >= startDate && d <= endDate;
        });
      } else {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - parseInt(dateRange));
      }
      if (dateRange !== 'last-month') {
        list = list.filter((t) => {
          const d = new Date(t.posted || t.transactedAt || 0);
          return d >= startDate;
        });
      }
    }

    if (accountFilter !== 'all') list = list.filter((t) => t.accountId === accountFilter);
    if (reviewFilter === 'reviewed') list = list.filter((t) => t.isReviewed);
    else if (reviewFilter === 'unreviewed') list = list.filter((t) => !t.isReviewed);
    if (categoryFilter !== 'all') list = list.filter((t) => t.category === categoryFilter);
    const _hideType = (cat) => cat && (hiddenTypeCats.has(cat) || [...hiddenTypeCats].some((c) => cat.startsWith(c + ':')));
    if (spendingTypeFilter === '') list = list.filter((t) => !t.spendingType && !_hideType(t.category));
    else if (spendingTypeFilter !== 'all') list = list.filter((t) => t.spendingType === spendingTypeFilter);
    if (applyScope === 'uncategorized') list = list.filter((t) => !t.category || (!t.spendingType && !_hideType(t.category)));
    else if (applyScope === 'unreviewed') list = list.filter((t) => !t.isReviewed);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          t.description.toLowerCase().includes(q) ||
          t.accountName.toLowerCase().includes(q) ||
          t.amount.includes(q) ||
          (t.memo && t.memo.toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc': return new Date(a.posted || a.transactedAt || 0) - new Date(b.posted || b.transactedAt || 0);
        case 'amount_desc': return parseFloat(b.amount) - parseFloat(a.amount);
        case 'amount_asc': return parseFloat(a.amount) - parseFloat(b.amount);
        case 'description': return a.description.localeCompare(b.description);
        default: return new Date(b.posted || b.transactedAt || 0) - new Date(a.posted || a.transactedAt || 0);
      }
    });

    return list;
  }, [transactions, search, sortBy, accountFilter, reviewFilter, categoryFilter, spendingTypeFilter, dateRange, applyScope, hiddenTypeCats]);

  // Spending summary for the filtered period
  const summary = useMemo(() => {
    const _notReportable = (cat) => cat && (notReportableCats.has(cat) || [...notReportableCats].some((c) => cat.startsWith(c + ':')));
    let spending = 0, income = 0, uncategorized = 0, unreviewed = 0;
    const byCat = {};
    for (const tx of filtered) {
      const amt = parseFloat(tx.amount) || 0;
      if (tx.isReportable !== false && !_notReportable(tx.category)) {
        if (amt < 0) {
          spending += Math.abs(amt);
          const cat = tx.category || 'Uncategorized';
          byCat[cat] = (byCat[cat] || 0) + Math.abs(amt);
        }
        if (amt > 0) income += amt;
      }
      if (!tx.category && amt < 0) uncategorized++;
      if (!tx.isReviewed) unreviewed++;
    }
    const topCats = Object.entries(byCat).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { spending, income, uncategorized, unreviewed, topCats };
  }, [filtered, notReportableCats]);

  const paged = filtered.slice(0, (page + 1) * PAGE_SIZE);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    for (const tx of transactions) {
      if (tx.tags) {
        for (const tag of tx.tags.split(',')) {
          const t = tag.trim();
          if (t) tagSet.add(t);
        }
      }
    }
    return [...tagSet].sort();
  }, [transactions]);

  function formatCurrency(amount) {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
    });
  }

  function formatDateShort(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: 'America/Chicago', month: 'short', day: 'numeric',
    });
  }

  function getDateGroup(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const dStr = d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
    const todayStr = today.toLocaleDateString('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
    const yestStr = yesterday.toLocaleDateString('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
    if (dStr === todayStr) return 'Today';
    if (dStr === yestStr) return 'Yesterday';
    return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', weekday: 'long', month: 'long', day: 'numeric' });
  }

  async function openDetail(tx) {
    setSelectedTx(tx);
    setMemoText(tx.memo || '');
    setEditingMemo(false);
  }

  function closeDetail() {
    setSelectedTx(null);
    setEditingMemo(false);
  }

  async function updateTransactionField(txId, updates) {
    try {
      await apiFetch(`/api/simplefin/transactions/${encodeURIComponent(txId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      setTransactions((prev) =>
        prev.map((t) => (t.id === txId ? { ...t, ...updates } : t))
      );
      if (selectedTx && selectedTx.id === txId) {
        setSelectedTx((prev) => ({ ...prev, ...updates }));
      }
    } catch (err) {
      console.error(err);
    }
  }

  function toggleReviewed(txId, current) {
    updateTransactionField(txId, { isReviewed: !current });
  }

  async function saveMemo() {
    if (!selectedTx) return;
    setSavingMemo(true);
    try {
      await apiFetch('/api/simplefin/transactions/memo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId: selectedTx.id, memo: memoText }),
      });
      setTransactions((prev) =>
        prev.map((t) => (t.id === selectedTx.id ? { ...t, memo: memoText } : t))
      );
      setSelectedTx((prev) => ({ ...prev, memo: memoText }));
      setEditingMemo(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingMemo(false);
    }
  }

  function openRuleModal(tx) {
    setRuleModal({
      pattern: tx.description,
      matchField: 'description',
      matchType: 'contains',
      category: tx.category || '',
      spendingType: tx.spendingType || '',
      applyToAll: true,
      txId: tx.id,
    });
  }

  async function saveRule() {
    if (!ruleModal || !ruleModal.pattern || (!ruleModal.category && !ruleModal.spendingType)) return;
    setSavingRule(true);
    try {
      // Create the rule
      await apiFetch('/api/category-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern: ruleModal.pattern,
          matchField: ruleModal.matchField,
          matchType: ruleModal.matchType,
          category: ruleModal.category,
          spendingType: ruleModal.spendingType,
          priority: 50,
        }),
      });

      // Apply to current transaction
      const updates = {};
      if (ruleModal.category) updates.category = ruleModal.category;
      if (ruleModal.spendingType) updates.spendingType = ruleModal.spendingType;
      await updateTransactionField(ruleModal.txId, updates);

      // Apply to all existing matching transactions
      if (ruleModal.applyToAll) {
        const res = await apiFetch('/api/category-rules/apply', { method: 'POST' });
        const data = await res.json();
        if (data.categorized > 0) {
          fetchData(); // reload to show updated categories
        }
      }

      setRuleModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingRule(false);
    }
  }

  async function handleCsvImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch('/api/simplefin/transactions/import-csv', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setImportResult(data);
        fetchData();
      } else {
        setImportResult({ errors: [data.error || 'Import failed'] });
      }
    } catch (err) {
      setImportResult({ errors: [err.message] });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  }

  async function handleApplyRules() {
    setApplyingRules(true);
    setApplyRulesResult(null);
    try {
      const res = await apiFetch('/api/category-rules/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: applyScope }),
      });
      if (res.ok) {
        const data = await res.json();
        setApplyRulesResult(`Categorized ${data.categorized} transaction${data.categorized !== 1 ? 's' : ''}`);
        if (data.categorized > 0) fetchData();
        setTimeout(() => setApplyRulesResult(null), 5000);
      }
    } catch (err) {
      setApplyRulesResult('Failed to apply rules');
    } finally {
      setApplyingRules(false);
    }
  }

  // Bulk actions
  function toggleSelect(txId) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(txId)) next.delete(txId);
      else next.add(txId);
      return next;
    });
  }

  function selectAll() {
    if (selectedIds.size === paged.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paged.map((t) => t.id)));
    }
  }

  async function applyBulkCategory() {
    for (const id of selectedIds) {
      const updates = {};
      if (bulkCategory) updates.category = bulkCategory;
      if (bulkSpendingType) updates.spendingType = bulkSpendingType;
      if (Object.keys(updates).length > 0) {
        await updateTransactionField(id, updates);
      }
    }
    setSelectedIds(new Set());
    setBulkCategory('');
    setBulkSpendingType('');
  }

  async function bulkMarkReviewed() {
    for (const id of selectedIds) {
      await updateTransactionField(id, { isReviewed: true });
    }
    setSelectedIds(new Set());
  }

  if (loading) {
    return (
      <section className="card">
        <h2>Transactions</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  // Build date-grouped display
  let lastGroup = '';

  return (
    <section className="card sfin-transactions-section">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Transactions</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            className={`nw-range-btn${bulkMode ? ' active' : ''}`}
            onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
            style={{ fontSize: 12 }}
          >
            {bulkMode ? 'Done' : 'Select'}
          </button>
          <select value={applyScope} onChange={(e) => setApplyScope(e.target.value)}
            className="sfin-tx-select" style={{ width: 'auto', fontSize: 12 }}>
            <option value="uncategorized">Uncategorized only</option>
            <option value="unreviewed">Unreviewed transactions</option>
            <option value="all">All transactions</option>
          </select>
          <button className="manage-btn" onClick={handleApplyRules} disabled={applyingRules} style={{ fontSize: 12 }}>
            {applyingRules ? 'Applying...' : 'Apply Rules'}
          </button>
          <label className="receipt-action-btn import-btn" style={{ cursor: importing ? 'wait' : 'pointer', opacity: importing ? 0.6 : 1 }}>
            {importing ? 'Importing...' : 'Import CSV'}
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvImport} disabled={importing} />
          </label>
        </div>
      </div>

      {applyRulesResult && (
        <div className="sfin-import-result sfin-import-success" style={{ marginBottom: 8 }}>
          <span>{applyRulesResult}</span>
          <button className="modal-close" onClick={() => setApplyRulesResult(null)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>&times;</button>
        </div>
      )}

      {importResult && (
        <div className={`sfin-import-result ${importResult.errors?.length ? 'sfin-import-error' : 'sfin-import-success'}`}>
          {importResult.errors?.length ? (
            <span>Import error: {importResult.errors.join(', ')}</span>
          ) : (
            <span>Import complete: {importResult.updated || 0} updated, {importResult.created || 0} created, {importResult.skipped || 0} unchanged</span>
          )}
          <button className="modal-close" onClick={() => setImportResult(null)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>&times;</button>
        </div>
      )}

      {/* Date range tabs */}
      <div className="nw-range-tabs" style={{ marginBottom: 8 }}>
        {DATE_RANGES.map((r) => (
          <button key={r.key} className={`nw-range-btn${dateRange === r.key ? ' active' : ''}`} onClick={() => setDateRange(r.key)}>
            {r.label}
          </button>
        ))}
      </div>

      {/* Spending summary bar */}
      <div className="stx-summary-bar">
        <div className="stx-summary-stat">
          <span className="stx-summary-value negative">{formatCurrency(-summary.spending)}</span>
          <span className="stx-summary-label">Spent</span>
        </div>
        <div className="stx-summary-stat">
          <span className="stx-summary-value positive">{formatCurrency(summary.income)}</span>
          <span className="stx-summary-label">Income</span>
        </div>
        <div className="stx-summary-stat">
          <span className={`stx-summary-value ${summary.income - summary.spending >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(summary.income - summary.spending)}
          </span>
          <span className="stx-summary-label">Net</span>
        </div>
        {summary.uncategorized > 0 && (
          <div className="stx-summary-stat">
            <span className="stx-summary-value" style={{ color: '#fbbf24' }}>{summary.uncategorized}</span>
            <span className="stx-summary-label">Uncategorized</span>
          </div>
        )}
        {summary.unreviewed > 0 && (
          <div className="stx-summary-stat">
            <span className="stx-summary-value" style={{ color: '#71717a' }}>{summary.unreviewed}</span>
            <span className="stx-summary-label">Unreviewed</span>
          </div>
        )}
      </div>

      {/* Top categories mini bar */}
      {summary.topCats.length > 0 && summary.spending > 0 && (
        <div className="stx-cat-minibar">
          {summary.topCats.map(([cat, val]) => (
            <div key={cat} className="stx-cat-segment" style={{ flex: val / summary.spending }}
              title={`${cat}: ${formatCurrency(val)}`}>
              <span className="stx-cat-seg-label">{cat}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="sfin-tx-controls">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..." className="sfin-tx-search" />
        <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)} className="sfin-tx-select">
          <option value="all">All Accounts</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="sfin-tx-select">
          <option value="all">All Categories</option>
          {CategoryOptions()}
          <option value="">Uncategorized</option>
        </select>
        <select value={spendingTypeFilter} onChange={(e) => setSpendingTypeFilter(e.target.value)} className="sfin-tx-select">
          <option value="all">All Types</option>
          {SPENDING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          <option value="">Untyped</option>
        </select>
        <select value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)} className="sfin-tx-select">
          <option value="all">All</option>
          <option value="reviewed">Reviewed</option>
          <option value="unreviewed">Unreviewed</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sfin-tx-select">
          {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {/* Bulk actions bar */}
      {bulkMode && selectedIds.size > 0 && (
        <div className="stx-bulk-bar">
          <span className="stx-bulk-count">{selectedIds.size} selected</span>
          <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} className="sfin-tx-select" style={{ width: 'auto' }}>
            <option value="">Category...</option>
            {CategoryOptions()}
          </select>
          <select value={bulkSpendingType} onChange={(e) => setBulkSpendingType(e.target.value)} className="sfin-tx-select" style={{ width: 'auto' }}>
            <option value="">Type...</option>
            {SPENDING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button className="manage-btn" onClick={applyBulkCategory} disabled={!bulkCategory && !bulkSpendingType}
            style={{ fontSize: 12, padding: '4px 10px' }}>Apply</button>
          <button className="manage-btn" onClick={bulkMarkReviewed} style={{ fontSize: 12, padding: '4px 10px', background: '#2a2b35' }}>
            Mark Reviewed
          </button>
        </div>
      )}

      <div className="sfin-tx-summary">
        {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
        {accountFilter !== 'all' && ` in ${accounts.find((a) => a.id === accountFilter)?.name || 'account'}`}
        {search && ` matching "${search}"`}
        {bulkMode && <span style={{ marginLeft: 8 }}>
          <button onClick={selectAll} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>
            {selectedIds.size === paged.length ? 'Deselect all' : 'Select all'}
          </button>
        </span>}
      </div>

      {/* Transaction list with date groups */}
      <div className="sfin-tx-list">
        {paged.length === 0 && <div className="receipts-empty">No transactions found</div>}
        {paged.map((tx) => {
          const amt = parseFloat(tx.amount);
          const isPositive = amt > 0;
          const dateStr = tx.posted || tx.transactedAt;
          const group = (sortBy === 'date_desc' || sortBy === 'date_asc') ? getDateGroup(dateStr) : null;
          const showGroup = group && group !== lastGroup;
          if (showGroup) lastGroup = group;

          return (
            <div key={tx.id}>
              {showGroup && <div className="stx-date-group">{group}</div>}
              <div className={`sfin-tx-row${tx.isReviewed ? ' sfin-tx-reviewed' : ''}${selectedIds.has(tx.id) ? ' stx-selected' : ''}`}>
                {/* Checkbox: bulk or review */}
                <div className="sfin-tx-check" onClick={(e) => {
                  e.stopPropagation();
                  if (bulkMode) toggleSelect(tx.id);
                  else toggleReviewed(tx.id, tx.isReviewed);
                }}>
                  <input type="checkbox" checked={bulkMode ? selectedIds.has(tx.id) : !!tx.isReviewed} readOnly />
                </div>

                {/* Main info — click to open detail */}
                <div className="sfin-tx-info" onClick={() => openDetail(tx)}>
                  <span className="sfin-tx-description">{tx.description}</span>
                  <div className="sfin-tx-tags">
                    <span className="sfin-tx-account">{tx.accountName}</span>

                    {/* Inline category — click to edit */}
                    {inlineCatTx === tx.id ? (
                      <select
                        className="sfin-tx-inline-select"
                        value={tx.category || ''}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { updateTransactionField(tx.id, { category: e.target.value }); setInlineCatTx(null); }}
                        onBlur={() => setInlineCatTx(null)}
                      >
                        <option value="">Uncategorized</option>
                        {CategoryOptions()}
                      </select>
                    ) : (
                      <span
                        className={`sfin-tx-category-badge${!tx.category ? ' stx-uncat' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setInlineCatTx(tx.id); setInlineStTx(null); }}
                        title={tx.category || 'Click to categorize'}
                      >
                        {getCategoryLabel(tx.category)}
                      </span>
                    )}

                    {/* Inline spending type — hidden for transfers */}
                    {!shouldHideSpendingType(tx.category) && (
                      inlineStTx === tx.id ? (
                        <select
                          className="sfin-tx-inline-select"
                          value={tx.spendingType || ''}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => { updateTransactionField(tx.id, { spendingType: e.target.value }); setInlineStTx(null); }}
                          onBlur={() => setInlineStTx(null)}
                        >
                          <option value="">No type</option>
                          {SPENDING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      ) : tx.spendingType ? (
                        <span
                          className={`sfin-tx-spending-badge sfin-st-${tx.spendingType}`}
                          onClick={(e) => { e.stopPropagation(); setInlineStTx(tx.id); setInlineCatTx(null); }}
                          title="Click to change type"
                        >
                          {SPENDING_TYPES.find(t => t.value === tx.spendingType)?.label || tx.spendingType}
                        </span>
                      ) : (
                        <span
                          className="sfin-tx-spending-badge stx-untyped"
                          onClick={(e) => { e.stopPropagation(); setInlineStTx(tx.id); setInlineCatTx(null); }}
                          title="Click to set type"
                        >
                          + type
                        </span>
                      )
                    )}

                    {tx.isBusiness && <span className="stx-memo-indicator" title="Business">&#x1F4BC;</span>}
                    {tx.isReportable === false && <span className="stx-memo-indicator" title="Not reportable" style={{ opacity: 0.5 }}>&#x1F6AB;</span>}
                    {tx.memo && <span className="stx-memo-indicator" title={tx.memo}>&#x1F4DD;</span>}
                    {tx.tags && tx.tags.split(',').filter(Boolean).map((tag) => (
                      <span key={tag} className="stx-tag-badge" style={{ fontSize: 10, padding: '0 4px' }}>{tag.trim()}</span>
                    ))}
                    {(!tx.category || !tx.isReviewed) && (
                      <button
                        className="stx-create-rule-btn"
                        onClick={(e) => { e.stopPropagation(); openRuleModal(tx); }}
                        title="Create categorization rule"
                      >
                        + rule
                      </button>
                    )}
                  </div>
                </div>

                {/* Amount & date */}
                <div className="sfin-tx-meta" onClick={() => openDetail(tx)}>
                  <span className={`sfin-tx-amount${isPositive ? ' positive' : ' negative'}`}>
                    {formatCurrency(tx.amount)}
                  </span>
                  <span className="sfin-tx-date">
                    {tx.pending ? 'Pending' : formatDateShort(dateStr)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {paged.length < filtered.length && (
        <div className="sfin-tx-pagination" style={{ textAlign: 'center', paddingRight: 12 }}>
          <button className="manage-btn" onClick={() => setPage((p) => p + 1)}
            style={{ width: '100%', padding: '8px 0', fontSize: 13 }}>
            Show More ({filtered.length - paged.length} remaining)
          </button>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTx && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal-content sfin-tx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>{selectedTx.description}</h2>
              <button className="modal-close" onClick={closeDetail}>&times;</button>
            </div>
            <div className="sfin-tx-detail-body">
              <div className="stx-detail-amount-row">
                <span className={`stx-detail-big-amount${parseFloat(selectedTx.amount) > 0 ? ' positive' : ' negative'}`}>
                  {formatCurrency(selectedTx.amount)}
                </span>
                <span className="stx-detail-date">
                  {selectedTx.pending ? 'Pending' : formatDate(selectedTx.posted || selectedTx.transactedAt)}
                </span>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Date</span>
                <div className="sfin-tx-detail-value">
                  <input type="date"
                    className="sfin-tx-category-select"
                    value={(selectedTx.posted || selectedTx.transactedAt || '').slice(0, 10)}
                    onChange={(e) => {
                      if (e.target.value) updateTransactionField(selectedTx.id, { posted: e.target.value + 'T00:00:00Z' });
                    }}
                  />
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Account</span>
                <span className="sfin-tx-detail-value">{selectedTx.accountName}</span>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Category</span>
                <div className="sfin-tx-detail-value">
                  <select className="sfin-tx-category-select" value={selectedTx.category || ''}
                    onChange={(e) => updateTransactionField(selectedTx.id, { category: e.target.value })}>
                    <option value="">Uncategorized</option>
                    {CategoryOptions()}
                  </select>
                </div>
              </div>
              {!shouldHideSpendingType(selectedTx.category) && (
                <div className="sfin-tx-detail-row">
                  <span className="sfin-tx-detail-label">Spending Type</span>
                  <div className="sfin-tx-detail-value">
                    <select className="sfin-tx-category-select" value={selectedTx.spendingType || ''}
                      onChange={(e) => updateTransactionField(selectedTx.id, { spendingType: e.target.value })}>
                      <option value="">Not set</option>
                      {SPENDING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Reviewed</span>
                <div className="sfin-tx-detail-value">
                  <label className="sfin-tx-reviewed-toggle">
                    <input type="checkbox" checked={!!selectedTx.isReviewed}
                      onChange={() => toggleReviewed(selectedTx.id, selectedTx.isReviewed)} />
                    <span>{selectedTx.isReviewed ? 'Reviewed' : 'Not reviewed'}</span>
                  </label>
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Reportable</span>
                <div className="sfin-tx-detail-value">
                  <label className="sfin-tx-reviewed-toggle">
                    <input
                      type="checkbox"
                      checked={selectedTx.isReportable !== false}
                      onChange={() => updateTransactionField(selectedTx.id, { isReportable: selectedTx.isReportable === false })}
                    />
                    <span>{selectedTx.isReportable !== false ? 'Reportable' : 'Not reportable'}</span>
                  </label>
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Business</span>
                <div className="sfin-tx-detail-value">
                  <label className="sfin-tx-reviewed-toggle">
                    <input type="checkbox" checked={!!selectedTx.isBusiness}
                      onChange={() => updateTransactionField(selectedTx.id, { isBusiness: !selectedTx.isBusiness })} />
                    <span>{selectedTx.isBusiness ? 'Yes' : 'No'}</span>
                  </label>
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Tags</span>
                <div className="sfin-tx-detail-value">
                  <TagsInput
                    tags={selectedTx.tags ? selectedTx.tags.split(',').filter(Boolean) : []}
                    allTags={allTags}
                    onChange={(newTags) => updateTransactionField(selectedTx.id, { tags: newTags.join(',') })}
                  />
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Memo</span>
                <div className="sfin-tx-detail-value sfin-tx-memo-area">
                  {editingMemo ? (
                    <div className="sfin-tx-memo-edit">
                      <textarea value={memoText} onChange={(e) => setMemoText(e.target.value)}
                        className="sfin-tx-memo-input" rows={3} placeholder="Add a memo..." />
                      <div className="sfin-tx-memo-actions">
                        <button className="receipt-action-btn import-btn" onClick={saveMemo} disabled={savingMemo}>
                          {savingMemo ? 'Saving...' : 'Save'}
                        </button>
                        <button className="receipt-action-btn ignore-btn"
                          onClick={() => { setEditingMemo(false); setMemoText(selectedTx.memo || ''); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="sfin-tx-memo-display" onClick={() => setEditingMemo(true)}>
                      {selectedTx.memo || <span className="sfin-tx-memo-placeholder">Click to add memo...</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Rule Modal */}
      {ruleModal && (
        <div className="modal-overlay" onClick={() => setRuleModal(null)}>
          <div className="modal-content stx-rule-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>Create Categorization Rule</h2>
              <button className="modal-close" onClick={() => setRuleModal(null)}>&times;</button>
            </div>
            <div className="stx-rule-form">
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">If description</span>
                <div className="sfin-tx-detail-value" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select value={ruleModal.matchType} className="sfin-tx-category-select" style={{ width: 'auto' }}
                    onChange={(e) => setRuleModal({ ...ruleModal, matchType: e.target.value })}>
                    <option value="contains">contains</option>
                    <option value="startsWith">starts with</option>
                    <option value="exact">exactly matches</option>
                  </select>
                  <input type="text" value={ruleModal.pattern}
                    onChange={(e) => setRuleModal({ ...ruleModal, pattern: e.target.value })}
                    className="sfin-tx-search" style={{ flex: 1 }} />
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Set Category</span>
                <div className="sfin-tx-detail-value">
                  <select className="sfin-tx-category-select" value={ruleModal.category}
                    onChange={(e) => setRuleModal({ ...ruleModal, category: e.target.value })}>
                    <option value="">Select category...</option>
                    {CategoryOptions()}
                  </select>
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Spending Type</span>
                <div className="sfin-tx-detail-value">
                  <select className="sfin-tx-category-select" value={ruleModal.spendingType}
                    onChange={(e) => setRuleModal({ ...ruleModal, spendingType: e.target.value })}>
                    <option value="">Not set</option>
                    {SPENDING_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sfin-tx-detail-row">
                <span className="sfin-tx-detail-label">Apply to existing</span>
                <div className="sfin-tx-detail-value">
                  <label className="sfin-tx-reviewed-toggle">
                    <input type="checkbox" checked={ruleModal.applyToAll}
                      onChange={(e) => setRuleModal({ ...ruleModal, applyToAll: e.target.checked })} />
                    <span>Apply this rule to all matching transactions</span>
                  </label>
                </div>
              </div>
              <div className="stx-rule-actions">
                <button className="manage-btn" onClick={() => setRuleModal(null)} style={{ background: '#2a2b35' }}>Cancel</button>
                <button className="manage-btn" onClick={saveRule}
                  disabled={savingRule || (!ruleModal.category && !ruleModal.spendingType)}>
                  {savingRule ? 'Creating...' : ruleModal.applyToAll ? 'Create Rule & Apply' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default TransactionsPanel;
